import React, { useEffect, useRef, useState } from "react";
import "./component_portfolio_look_picker.less";

export type PortfolioVisualization = "classic" | "editorial";

const LOOKS: Array<{
  value: PortfolioVisualization;
  label: string;
  description: string;
}> = [
  {
    value: "editorial",
    label: "Editorial",
    description: "Bold, visual storytelling.",
  },
  {
    value: "classic",
    label: "Classic",
    description: "Original BEAUTISTA layout.",
  },
];

type Props = {
  stored: string | null | undefined;
  canvasType?: string | null;
  disabled?: boolean;
  onChange: (visualization: PortfolioVisualization) => void;
};

function resolveLook(
  stored: string | null | undefined,
  canvasType?: string | null
): PortfolioVisualization {
  if (stored === "classic" || stored === "editorial") {
    return stored;
  }
  // Match canvas-frontend school default when editorial ship is on.
  if ((canvasType || "").toLowerCase() === "school") {
    return "editorial";
  }
  return "classic";
}

export default function PortfolioLookPicker({
  stored,
  canvasType,
  disabled = false,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = resolveLook(stored, canvasType);
  const currentLook = LOOKS.find((look) => look.value === current) ?? LOOKS[0];

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selectLook = (value: PortfolioVisualization) => {
    if (value !== current) {
      onChange(value);
    }
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="portfolio-look-picker">
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="portfolio-look-picker__trigger"
      >
        ✦ Change look · {currentLook.label}
      </button>

      {open ? (
        <div
          className="portfolio-look-picker__popover"
          role="dialog"
          aria-label="Portfolio look"
        >
          <div className="portfolio-look-picker__header">
            <div>
              <strong>Portfolio look</strong>
              <p>Choose how your portfolio is presented.</p>
            </div>
            <button
              type="button"
              className="portfolio-look-picker__close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div role="radiogroup" aria-label="Portfolio look options">
            {LOOKS.map((look) => {
              const selected = look.value === current;
              return (
                <button
                  key={look.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`portfolio-look-picker__option${
                    selected ? " is-selected" : ""
                  }`}
                  onClick={() => selectLook(look.value)}
                >
                  <LookThumb variant={look.value} />
                  <span className="portfolio-look-picker__copy">
                    <strong>{look.label}</strong>
                    <span>{look.description}</span>
                  </span>
                  <span
                    className={`portfolio-look-picker__check${
                      selected ? "" : " is-hidden"
                    }`}
                    aria-hidden
                  >
                    ✓
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LookThumb({ variant }: { variant: PortfolioVisualization }) {
  if (variant === "editorial") {
    return (
      <span className="portfolio-look-picker__thumb editorial" aria-hidden>
        <span />
        <span />
      </span>
    );
  }
  return <span className="portfolio-look-picker__thumb classic" aria-hidden />;
}
